import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { MultiSelect } from '../components/ui/multi-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import { UnifiedCard } from '../components/ui/unified-card';
import { ArchiveProject } from '../components/ArchiveProject';
import { DeleteProject } from '../components/DeleteProject';
import { RestoreProject } from '../components/RestoreProject';
import {
  Plus,
  Search,
  Filter,
  Calendar,
  GitBranch,
  Heart,
  Eye,
  FileText,
  Edit,
  Trash2,
  Archive,
  Package,
  Activity,
  FolderOpen,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArchiveRestore,
  User,
  Clock,
  Component
} from 'lucide-react';
import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatMemberSinceDate, getPriorityColor, getStatusColor, isSubscriptionValid } from '@/helpers/common';
import { useProjects } from "../hooks/useProjects";
import { useApplicationList } from "../hooks/useApplications";
import { FindProjectsOptions, Project, ProjectAPI } from "../api/projects";
import { ProjectForm } from '@/components/ProjectForm';
import { Paginator } from '@/components/Paginator';
import { useAuth } from '@/contexts/AuthContext';
import { useHasPermission } from '@/hooks/useHasPermission';

interface ProjectsProps {
  isGridLayout: boolean;
  workgroupId: number;
  onNavigate: (route: string, params?: any) => void;
  applicationId?: number;
  onRefreshProjects?: (refreshFn: () => void) => void;
}

export function Projects({ isGridLayout, onNavigate, workgroupId, applicationId: propApplicationId, onRefreshProjects }: ProjectsProps) {
  const { subscription } = useAuth();
  const canCreate = useHasPermission('projects', 'create');
  const canUpdate = useHasPermission('projects', 'update');
  const canDelete = useHasPermission('projects', 'delete');
  const canRestore = useHasPermission('projects', 'restore');
  const canFavourite = useHasPermission('projects', 'favourite');
  const { applicationId: urlApplicationId } = useParams<{ applicationId?: string }>();
  const [searchParams] = useSearchParams();
  const queryApplicationId = searchParams.get('applicationId');
  const applicationId = propApplicationId || (urlApplicationId ? parseInt(urlApplicationId) : (queryApplicationId ? parseInt(queryApplicationId) : undefined));

  useEffect(() => {
    setSearchTerm('');
    setSelectedApplications([]);
    setCurrentPage(1);
    setStatusFilter('all');
    setFavouriteFilter('all');
    setProviderFilter('all');
    setRepoTypeFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  }, [applicationId]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedApplications, setSelectedApplications] = useState<(string | number)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationSearchTerm, setApplicationSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [favouriteFilter, setFavouriteFilter] = useState<string>('all');
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [repoTypeFilter, setRepoTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { data: allProjects, loading, error, refetch } = useProjects({
    workgroupId,
    applicationId: applicationId,
  } as FindProjectsOptions);

  const getSortDisplayTitle = (sortKey: string) => {
    const sortTitles: Record<string, string> = {
      'name': 'Name',
      'lastactivityon': 'Last Activity',
      'overallRiskScore': 'Risk Score'
    };
    return sortTitles[sortKey] || sortKey;
  };

  const {
    filteredProjects,
    paginatedProjects,
  } = useMemo(() => {
    const filtered = allProjects?.filter(project => {

      const searchMatch = searchTerm === '' ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.repoprovider?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.repotype?.toLowerCase().includes(searchTerm.toLowerCase());

      const applicationMatch = selectedApplications.length === 0 ||
        selectedApplications.includes(project.applicationid) ||
        (project.applications && selectedApplications.includes(project.applications.id));


      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'Active' && project.isactive) ||
        (statusFilter === 'Archived' && project.isarchived) ||
        (statusFilter === 'Inactive' && !project.isactive && !project.isarchived);


      const favouriteMatch = favouriteFilter === 'all' ||
        (favouriteFilter === 'favourites' && project.isfavourite) ||
        (favouriteFilter === 'non-favourites' && !project.isfavourite);


      const providerMatch = providerFilter === 'all' ||
        project.repoprovider === providerFilter;


      const repoTypeMatch = repoTypeFilter === 'all' ||
        project.repotype === repoTypeFilter;

      return searchMatch && applicationMatch && statusMatch && favouriteMatch && providerMatch && repoTypeMatch;
    }) || [];

    const sorted = [...filtered].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'createdon':
          aValue = a.createdon;
          bValue = b.createdon;
          break;
        case 'lastactivityon':
          aValue = a.lastactivityon || a.createdon;
          bValue = b.lastactivityon || b.createdon;
          break;

        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    const itemsPerPage = 9;
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = sorted.slice(startIndex, endIndex);

    return {
      filteredProjects: filtered,
      paginatedProjects: paginated,
    };
  }, [allProjects, searchTerm, selectedApplications, currentPage, sortBy, sortOrder, statusFilter, favouriteFilter, providerFilter, repoTypeFilter, applicationId]);

  const {
    data: applications,
    loading: applicationsLoading
  } = useApplicationList(workgroupId);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleApplicationFilterChange = (applications: (string | number)[]) => {
    setSelectedApplications(applications);
    setCurrentPage(1);
  };

  const handleApplicationSearch = (searchTerm: string) => {
    setApplicationSearchTerm(searchTerm);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleFavouriteFilter = (filter: string) => {
    setFavouriteFilter(filter);
    setCurrentPage(1);
  };

  const handleProviderFilter = (provider: string) => {
    setProviderFilter(provider);
    setCurrentPage(1);
  };

  const handleRepoTypeFilter = (repoType: string) => {
    setRepoTypeFilter(repoType);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setSelectedApplications([]);
    setStatusFilter('all');
    setFavouriteFilter('all');
    setProviderFilter('all');
    setRepoTypeFilter('all');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleNewProject = () => {
    setSelectedProject(null);
    setEditModalOpen(true);
  };

  const handleViewScans = (projectId: number) => {
    onNavigate('scans', { projectId });
  };
  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setEditModalOpen(true);
  };

  const handleArchiveProject = (project: Project) => {
    setSelectedProject(project);
    setArchiveModalOpen(true);
  };

  const handleDeleteProject = (project: Project) => {
    setSelectedProject(project);
    setDeleteModalOpen(true);
  };

  const handleRestoreProject = (project: Project) => {
    setSelectedProject(project);
    setRestoreModalOpen(true);
  };

  const handleModalSuccess = () => {
    refetch();
  };

  const handleToggleFavorite = async (project: Project) => {
    try {
      await ProjectAPI.favourite(project.id, !project.isfavourite);
      refetch();
    } catch (error) {
    }
  };

  const applicationOptions = applications?.map(app => ({
    value: app.id,
    label: app.name
  })) || [];

  const hasActiveFilters = selectedApplications.length > 0 ||
    statusFilter !== 'all' ||
    favouriteFilter !== 'all' ||
    providerFilter !== 'all' ||
    repoTypeFilter !== 'all' ||
    searchTerm.length > 0;

  const ProjectCard = ({ project }: { project: Project }) => {
    // Clicking the card does the same as "View Scans".
    const cardLink = {
      onClick: () => handleViewScans(project.id),
      href: `/scans/${project.id}`
    };

    const gridProps = {
      ...cardLink,
      title: project.name,
      description: project.description || 'No description available',
      badges: [
        {
          label: (project.isarchived ? 'Archived' : project.isactive ? 'Active' : 'Inactive'),
          className: getStatusColor((project.isarchived ? 'Archived' : project.isactive ? 'Active' : 'Inactive'))
        },
        ...(project.isfavourite ? [{
          label: 'Favourite',
          className: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }] : []),
      ],
      metrics: [
        {
          label: `${project.application_name}`,
          value: '',
          icon: <Package className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.repoprovider}`,
          value: '',
          icon: <GitBranch className="w-4 h-4 mr-1" />
        },

        {
          label: `${project.total_scan_count || project.scans?.length || 0} Scans`,
          value: '',
          icon: <Activity className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.total_component_count || 0} Components`,
          value: '',
          icon: <Component className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.lastactivityon ? 'Last: ' + new Date(project.lastactivityon).toLocaleDateString() : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        },
      ],


      riskScores: {
        overallScore: project.avg_overall_score || 0,
        vulnerabilityScore: project.avg_vulnerability_score || 0,
        licenseScore: project.avg_license_score || 0,
        operationalRisk: project.avg_operational_risk_score || 0,
        technicalDebt: project.avg_technical_debt_score || 0,
        businessRisk: project.avg_business_risk_score || 0,
        criticalVulnerabilities: project.critical_vulnerabilities || 0,
        highVulnerabilities: project.high_vulnerabilities || 0,
        mediumVulnerabilities: project.medium_vulnerabilities || 0,
        lowVulnerabilities: project.low_vulnerabilities || 0,
        criticalLicenseIssues: project.critical_license_issues || 0,
        highLicenseIssues: project.high_license_issues || 0,
        mediumLicenseIssues: project.medium_license_issues || 0,
        lowLicenseIssues: project.low_license_issues || 0
      },
      actions: [
        ...(canFavourite ? [{
          label: project.isfavourite ? 'Remove from Favourites' : 'Add to Favourites',
          icon: <Heart className={`w-4 h-4 mr-2 ${project.isfavourite ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />,
          onClick: () => handleToggleFavorite(project)
        }] : []),
        {
          label: 'View Scans',
          icon: <Eye className="w-4 h-4 mr-2" />,
          onClick: () => handleViewScans(project.id)
        },
        {
          label: 'View Report',
          icon: <FileText className="w-4 h-4 mr-2" />,
          onClick: () => onNavigate('project-view-report', { projectId: project.id })
        },
        ...(canUpdate ? [{
          label: 'Edit Project',
          icon: <Edit className="w-4 h-4 mr-2" />,
          onClick: () => handleEditProject(project)
        }] : []),
        ...(project.isarchived
          ? (canRestore ? [{
              label: 'Restore Project',
              icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
              onClick: () => handleRestoreProject(project),
              variant: 'default' as const
            }] : [])
          : (canDelete ? [{
              label: 'Archive Project',
              icon: <Archive className="w-4 h-4 mr-2" />,
              onClick: () => handleArchiveProject(project),
              variant: 'danger' as const
            }] : [])),
        ...(canDelete ? [{
          label: 'Delete Project',
          icon: <Trash2 className="w-4 h-4 mr-2" />,
          onClick: () => handleDeleteProject(project),
          variant: 'danger' as const
        }] : [])
      ]
    };
    const listProps = {
      ...cardLink,
      title: project.name,
      description: project.description,
      badges: [
        {
          label: (project.isarchived ? 'Archived' : project.isactive ? 'Active' : 'Inactive'),
          className: getStatusColor((project.isarchived ? 'Archived' : project.isactive ? 'Active' : 'Inactive'))
        },
        ...(project.isfavourite ? [{
          label: 'Favourite',
          className: 'bg-red-100 text-red-800 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }] : []),

      ],
      metrics: [
        {
          label: `${project.total_scan_count || project.scans?.length || 0}`,
          value: '',
          icon: <Activity className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.total_component_count || 0}`,
          value: '',
          icon: <Component className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.creator_firstname}`,
          value: '',
          icon: <User className="w-4 h-4 mr-1" />
        },
        {
          label: `${project.lastactivityon ? new Date(project.lastactivityon).toLocaleDateString() : 'No activity'}`,
          value: '',
          icon: <Calendar className="w-4 h-4 mr-1" />
        }
      ],

      riskScores: {
        overallScore: project.avg_overall_score || 0,
        vulnerabilityScore: project.avg_vulnerability_score || 0,
        licenseScore: project.avg_license_score || 0,
        operationalRisk: project.avg_operational_risk_score || 0,
        technicalDebt: project.avg_technical_debt_score || 0,
        businessRisk: project.avg_business_risk_score || 0,
        criticalVulnerabilities: project.critical_vulnerabilities || 0,
        highVulnerabilities: project.high_vulnerabilities || 0,
        mediumVulnerabilities: project.medium_vulnerabilities || 0,
        lowVulnerabilities: project.low_vulnerabilities || 0,
        criticalLicenseIssues: project.critical_license_issues || 0,
        highLicenseIssues: project.high_license_issues || 0,
        mediumLicenseIssues: project.medium_license_issues || 0,
        lowLicenseIssues: project.low_license_issues || 0
      },

      actions: [
        ...(canFavourite ? [{
          label: project.isfavourite ? 'Remove from Favourites' : 'Add to Favourites',
          icon: <Heart className={`w-4 h-4 mr-2 ${project.isfavourite ? 'fill-red-500 text-red-500' : 'text-gray-500'}`} />,
          onClick: () => handleToggleFavorite(project)
        }] : []),
        {
          label: 'View Scans',
          icon: <Eye className="w-4 h-4 mr-2" />,
          onClick: () => handleViewScans(project.id)
        },
        {
          label: 'View Report',
          icon: <FileText className="w-4 h-4 mr-2" />,
          onClick: () => onNavigate('project-view-report', { projectId: project.id })
        },
        ...(canUpdate ? [{
          label: 'Edit Project',
          icon: <Edit className="w-4 h-4 mr-2" />,
          onClick: () => handleEditProject(project)
        }] : []),
        ...(project.isarchived
          ? (canRestore ? [{
              label: 'Restore Project',
              icon: <ArchiveRestore className="w-4 h-4 mr-2" />,
              onClick: () => handleRestoreProject(project),
              variant: 'default' as const
            }] : [])
          : (canDelete ? [{
              label: 'Archive Project',
              icon: <Archive className="w-4 h-4 mr-2" />,
              onClick: () => handleArchiveProject(project),
              variant: 'danger' as const
            }] : [])),
        ...(canDelete ? [{
          label: 'Delete Project',
          icon: <Trash2 className="w-4 h-4 mr-2" />,
          onClick: () => handleDeleteProject(project),
          variant: 'danger' as const
        }] : [])
      ]
    };

    return (
      <UnifiedCard
        key={project.id}
        isGridLayout={isGridLayout}
        gridProps={gridProps}
        listProps={listProps}
        className={"gap-0"}
      >
        {isGridLayout ? (
          <>


            <div className="mb-4 hidden sm:block">
              <p className="text-sm text-muted-foreground mb-2 font-medium">Technology</p>
              <div className="flex flex-wrap gap-1">
                {project.technology && project.technology.length > 2 ? (

                  (JSON.parse(project.technology) as string[])?.map((tech, index) => (
                    <Badge key={index} variant="outline" className="text-xs bg-muted">
                      {tech}
                    </Badge>
                  ))
                ) : (
                  <Badge variant="outline" className="text-xs bg-muted">
                    Not specified
                  </Badge>
                )}
              </div>
            </div>
          </>
        ) : (<>
          <div className="flex items-center space-x-2 flex-shrink-0">

            <div className="text-center">
              <Badge variant="outline" className="bg-muted" title='Application Name'>
                <Package className="w-4 h-4 mr-1" />
                {project.application_name}
              </Badge>
            </div>

            <div className="text-center">
              <Badge variant="outline" className="bg-muted" title='Provider'>
                <GitBranch className="w-4 h-4 mr-1" />
                {project.repoprovider}
              </Badge>
            </div>
          </div>



        </>)}
      </UnifiedCard>
    );
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center">Loading projects...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 space-y-6">
        <div className="text-center text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-2 max-w-full" style={{ paddingTop: "10px" }}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center gap-2 justify-center sm:justify-end">
          <Badge
            variant="secondary"
            className="cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-xs whitespace-nowrap"
            onClick={() => setSelectedApplications([])}
            title="Click to clear all filters"
          >
            <span className="whitespace-nowrap">{allProjects?.length || 0} Total</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Active'
              ? 'bg-green-200 text-green-900 border-green-300 hover:bg-green-300 dark:bg-green-800 dark:text-green-100'
              : 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900 dark:text-green-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Active' ? 'all' : 'Active')}
            title="Click to filter by Active status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allProjects?.filter((p: Project) => p.isactive).length || 0}</span>
            <span className="whitespace-nowrap">Active</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${favouriteFilter === 'favourites'
              ? 'bg-red-200 text-red-900 border-red-300 hover:bg-red-300 dark:bg-red-800 dark:text-red-100'
              : 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900 dark:text-red-200'
              }`}
            onClick={() => handleFavouriteFilter(favouriteFilter === 'favourites' ? 'all' : 'favourites')}
            title="Click to filter by Favourites"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allProjects?.filter((p: Project) => p.isfavourite).length || 0}</span>
            <span className="whitespace-nowrap">Favourite</span>
          </Badge>
          <Badge
            className={`cursor-pointer transition-colors flex-shrink-0 ${statusFilter === 'Archived'
              ? 'bg-yellow-200 text-yellow-900 border-yellow-300 hover:bg-yellow-300 dark:bg-yellow-800 dark:text-yellow-100'
              : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 dark:bg-yellow-900 dark:text-yellow-200'
              }`}
            onClick={() => handleStatusFilter(statusFilter === 'Archived' ? 'Active' : 'Archived')}
            title="Click to filter by Archived status"
          >
            <span className="text-xs sm:text-sm whitespace-nowrap">{allProjects?.filter((p: Project) => p.isarchived).length || 0}</span>
            <span className="whitespace-nowrap">Archived</span>
          </Badge>
          {selectedApplications.length > 0 && (
            <Badge
              className="cursor-pointer hover:bg-purple-200 transition-colors bg-purple-100 text-purple-800"
              onClick={() => setSelectedApplications([])}
              title="Click to clear application filter"
            >
              {selectedApplications.length} App{selectedApplications.length !== 1 ? 's' : ''} Selected
            </Badge>
          )}

        </div>

        <div className="flex flex-col md:flex-row md:items-start gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 min-w-0 flex-1">
            <div className="relative w-full xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground z-10" />
              <Input
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 w-full bg-white rounded-lg shadow-sm border-border text-sm"
                maxLength={200}
              />
            </div>
            {!applicationId && (
              <div className="relative w-full xs:flex-1 sm:max-w-xs">
                <MultiSelect
                  options={applicationOptions}
                  value={selectedApplications}
                  onChange={handleApplicationFilterChange}
                  onSearch={handleApplicationSearch}
                  placeholder="Filter by applications..."
                  searchPlaceholder="Search applications..."
                  loading={applicationsLoading}
                  maxDisplayed={2}
                  className="bg-background rounded-lg shadow-sm border-border"
                />
              </div>
            )}
            <div className="relative w-full flex xs:flex-1 sm:max-w-xs lg:max-w-sm xl:max-w-md gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border flex-shrink-0 ${(statusFilter !== 'all' || favouriteFilter !== 'all') ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <Filter className="w-4 h-4 mr-1 sm:mr-2" />
                    <span>Filters</span>
                    {(statusFilter !== 'all' || favouriteFilter !== 'all' || providerFilter !== 'all' || repoTypeFilter !== 'all') && (
                      <Badge variant="secondary" className="ml-1 sm:ml-2 text-xs">
                        {[statusFilter, favouriteFilter, providerFilter, repoTypeFilter].filter(f => f !== 'all').length}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-80">
                  <DropdownMenuLabel>Filter Projects</DropdownMenuLabel>
                  <DropdownMenuSeparator />

                  <div className="px-2 py-1">
                    <div className="text-sm font-medium text-muted-foreground mb-2">Status</div>
                    <div className="grid grid-cols-3 gap-1">
                      {['all', 'Active', 'Archived'].map((status) => (
                        <DropdownMenuItem
                          key={status}
                          onClick={() => handleStatusFilter(status)}
                          className={`text-sm ${statusFilter === status
                            ? 'bg-blue-100 text-blue-900 dark:bg-blue-900 dark:text-blue-100'
                            : ''
                            }`}
                        >
                          {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                          {statusFilter === status && <div className="ml-auto w-2 h-2 bg-blue-500 rounded-full" />}
                        </DropdownMenuItem>
                      ))}
                    </div>
                  </div>

                  {(statusFilter !== 'all' || favouriteFilter !== 'all' || providerFilter !== 'all' || repoTypeFilter !== 'all') && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={clearAllFilters}
                        className="text-sm text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear Filters
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className={`bg-white rounded-lg shadow-sm border-border ${sortBy !== 'name' ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : ''
                      }`}
                  >
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    Sort: {getSortDisplayTitle(sortBy)}
                    {sortOrder === 'asc' ? <ArrowUp className="w-3 h-3 ml-1" /> : <ArrowDown className="w-3 h-3 ml-1" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => handleSort('name')}>Name</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('lastactivityon')}>Last Activity</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleSort('overallRiskScore')}>Risk Score</DropdownMenuItem>

                </DropdownMenuContent>
              </DropdownMenu>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <X className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Clear All</span>
                  <span className="sm:hidden">Clear</span>
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-4 xl:shrink-0">

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {canCreate && isSubscriptionValid(subscription?.validupto || null) && (
                <Button
                  onClick={handleNewProject}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg shadow-lg shadow-primary/20"
                >
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span>New Project</span>
                </Button>
              )}
            </div>
          </div>
        </div>

      </div>


      <div className={isGridLayout ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-4"}>
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            Loading projects...
          </div>
        ) : error ? (
          <div className="col-span-full text-center py-8 text-red-500">
            Error: {error}
          </div>
        ) : paginatedProjects.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            {searchTerm || selectedApplications.length > 0
              ? 'No projects found matching your filters.'
              : 'No projects found.'}
          </div>
        ) : (
          paginatedProjects.map((project: Project) => (
            <ProjectCard key={project.id} project={project} />
          ))
        )}
      </div>

      <Paginator
        totalItems={filteredProjects.length}
        currentPage={currentPage}
        perPage={9}
        onPageChange={handlePageChange}
        className="mt-6"
      />


      <ProjectForm
        isOpen={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        project={selectedProject}
        workgroupId={workgroupId}
        applicationId={applicationId}
        onSuccess={handleModalSuccess}

      />

      <ArchiveProject
        isOpen={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        project={selectedProject}
        onSuccess={handleModalSuccess}
      />

      <DeleteProject
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        project={selectedProject}
        onSuccess={handleModalSuccess}
      />

      <RestoreProject
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        project={selectedProject}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
}